"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.NestedRowStore = void 0;
var tslib_1 = require("tslib");
var Utils_1 = require("../Utils");
/**
 * Row store designed for nested rows.
 * It provides a getItems implementation, subclasses just need to provide implementation for a few abstract methods.
 *
 * Note: This row store supports only two levels of hierarchy.
 */
var NestedRowStore = /** @class */ (function () {
    /**
     * GROUP: Constructor
     */
    /**
     * @param {T[]} items - The full domain of items regardless of visibility (parent expand/collapse state).
     * @param {RowExpansionHandler} rowExpansionHandler - A function that handles attempts to change a row expansion state.
     */
    function NestedRowStore(items, rowExpansionHandler, expandedRowKeys) {
        /**
         * GROUP: Members
         */
        /** reference to full domain of items. */
        this.items = [];
        /** Helper structure for quick look ups using keys. */
        this.itemInfoLookup = {};
        /** The rows representing the visible items in the grid (contains top level items and children of expanded parents) */
        this.rows = [];
        this.rowExpansionHandler = rowExpansionHandler;
        Utils_1.assert(!!this.rowExpansionHandler, 'NestedRowStore expects a non null/undefined value for rowExpansionHandler');
        this.expandedRowKeys = expandedRowKeys || {};
        this.setItems(items);
    }
    NestedRowStore.prototype.setItems = function (items) {
        var _this = this;
        this.items = items || [];
        this.rows = [];
        this.itemInfoLookup = {};
        // build the rows and the itemInfoLookup
        var rowIndex = 0;
        this.items.forEach(function (item, index) {
            // create itemInfo for all domain items
            var itemInfo = _this.createRowItemInfo(index);
            _this.itemInfoLookup[_this.getKey(item)] = itemInfo;
            // create a row if item is visible
            if (_this.shouldBeVisible(item)) {
                _this.rows.push(_this.buildRowFromItem(item));
                itemInfo.gridRowIndex = rowIndex;
                rowIndex++;
            }
        });
    };
    NestedRowStore.prototype.createRowItemInfo = function (domainIndex, rowIndex) {
        if (rowIndex === void 0) { rowIndex = -1; }
        return {
            domainIndex: domainIndex,
            domainItem: this.items[domainIndex],
            numChildren: this.countChildrenOfItemAtIndex(this.items, domainIndex),
            gridRowIndex: rowIndex
        };
    };
    /**
     * GROUP: Public functions
     */
    /**
     * @param {number} start - inclusive, not guaranteed to be a positive number
     * @param {number} end - inclusive
     */
    NestedRowStore.prototype.getItems = function (start, end, rowHandler) {
        rowHandler(this.rows);
    };
    NestedRowStore.prototype.getRowItemInfo = function (key) {
        return this.itemInfoLookup[key];
    };
    NestedRowStore.prototype.getRowItemInfoForRowIndex = function (rowIndex) {
        var key = this.rows[rowIndex].key;
        return this.itemInfoLookup[key];
    };
    /**
     * @param {number} gridRowIndex - the row index within the grid
     * @returns {number} - the domain index of the item displayed at gridRowIndex.
     */
    NestedRowStore.prototype.getDomainIndexFromGridRowIndex = function (gridRowIndex) {
        Utils_1.assert(gridRowIndex >= 0 && gridRowIndex <= this.rows.length, 'visibleItemIndex needs to be a valid index');
        var row = this.rows[gridRowIndex];
        var itemInfo = this.itemInfoLookup[row.key];
        if (!!itemInfo) {
            return itemInfo.domainIndex;
        }
        return -1;
    };
    NestedRowStore.prototype.getGridRowIndexFromDomainIndex = function (domainIndex) {
        var item = this.items[domainIndex];
        var itemInfo = this.itemInfoLookup[this.getKey(item)];
        if (!!itemInfo) {
            return itemInfo.gridRowIndex;
        }
        return -1;
    };
    NestedRowStore.prototype.setExpandedRows = function (keys) {
        this.expandedRowKeys = keys;
        this.updateRowsForExpansion();
        return this;
    };
    /**
     * GROUP: Internal functions
     */
    NestedRowStore.prototype.isExpanded = function (item) {
        return this.expandedRowKeys[this.getKey(item)] !== undefined;
    };
    NestedRowStore.prototype.isRootItem = function (item) {
        var parentKey = this.getParentKey(item);
        return parentKey === undefined || parentKey === null;
    };
    NestedRowStore.prototype.isParentExpanded = function (item) {
        var parentKey = this.getParentKey(item);
        return (!!parentKey) ? this.expandedRowKeys[parentKey] !== undefined : false;
    };
    NestedRowStore.prototype.shouldBeVisible = function (item) {
        return (this.isRootItem(item) || this.isParentExpanded(item));
    };
    NestedRowStore.prototype.getNestingLevel = function (item) {
        var nestingLevel = 0;
        var parentKey = this.getParentKey(item);
        while (parentKey) {
            nestingLevel++;
            var itemInfo = this.itemInfoLookup[parentKey];
            Utils_1.assert(!!itemInfo, 'no matching item found for the specified parentKey');
            var parentItem = itemInfo.domainItem;
            Utils_1.assert(!!parentItem, 'no matching item found for the specified parentKey');
            parentKey = this.getParentKey(parentItem);
        }
        return nestingLevel;
    };
    /**
     * Updates rows[] to account for expanded/collapsed items. Uses splice to add/remove child rows
     * and also updates the gridRowIndexes inside itemInfoLookup for all affected rows.
     */
    NestedRowStore.prototype.updateRowsForExpansion = function () {
        var rowIndex = 0;
        var domainIndex = 0;
        var numDomainItems = this.items.length;
        while (domainIndex < numDomainItems) {
            var item = this.items[domainIndex];
            var key = this.getKey(item);
            Utils_1.assert(key === this.rows[rowIndex].key, 'current item key does not match the current row key.');
            var itemInfo = this.itemInfoLookup[key];
            Utils_1.assert(!!itemInfo, 'this item has not been inserted into the item info lookup.');
            if (this.isRootItem(item)) {
                itemInfo.gridRowIndex = rowIndex;
                if (itemInfo.numChildren > 0) {
                    if (this.isRowExpanded(key)) {
                        this.expandRowAtIndex(rowIndex);
                        rowIndex += itemInfo.numChildren;
                    }
                    else {
                        this.collapseRowAtIndex(rowIndex);
                    }
                    domainIndex += itemInfo.numChildren;
                }
            }
            domainIndex++;
            rowIndex++;
        }
        this.rows = this.rows.splice(0);
    };
    /**
     * Inserts the children of the given row into the rows array and updates their itemInfo
     */
    NestedRowStore.prototype.expandRowAtIndex = function (rowIndex) {
        if (this.childRowsExist(rowIndex)) {
            this.updateChildGridRowIndexes(rowIndex);
            return;
        }
        this.setRowIsExpanded(rowIndex, true);
        // update the corresponding itemInfoLookup elements and build child rows if necessary
        var childRows = [];
        var parentKey = this.rows[rowIndex].key;
        var itemInfo = this.itemInfoLookup[parentKey];
        for (var i = 0; i < itemInfo.numChildren; i++) {
            var childDomainItem = this.items[itemInfo.domainIndex + 1 + i];
            var childItemInfo = this.itemInfoLookup[this.getKey(childDomainItem)];
            Utils_1.assert(!!childItemInfo, 'the item has not been inserted into the item info lookup.');
            Utils_1.assert(parentKey === this.getParentKey(childDomainItem), 'the item is not a child of this parent.');
            childItemInfo.gridRowIndex = rowIndex + 1 + i;
            childRows.push(this.buildRowFromItem(childDomainItem));
        }
        // insert the children into the rows array
        if (childRows.length > 0) {
            // TFSID: 759339 - Maximum call stack depth exceed when search in groups dialog on web
            // we cannot use "this.rows.splice(rowIndex + 1, 0, ...childRows)" due to the ... calling apply
            // it turns it into "this.rows.splice.apply(this.rows, [rowIndex + 1, 0].concat(childRows))"
            // and apply turns it into this.splice(rowIndex+1,0,childRows[0],childRows[1],...,childRows[childRows.length - 1])
            // we then run out of stack space after we pass in > 125,000 or so items (at least thats aproximately the number for chrome)
            // working around it with slice + concat
            var insertionIndex = rowIndex + 1;
            this.rows = this.rows.slice(0, insertionIndex).concat(childRows, this.rows.slice(insertionIndex));
        }
    };
    NestedRowStore.prototype.updateChildGridRowIndexes = function (parentRowIndex) {
        var parentInfo = this.getRowItemInfoForRowIndex(parentRowIndex);
        for (var i = 0; i < parentInfo.numChildren; i++) {
            var childDomainItem = this.items[parentInfo.domainIndex + 1 + i];
            var childItemInfo = this.itemInfoLookup[this.getKey(childDomainItem)];
            Utils_1.assert(!!childItemInfo, 'the item has not been inserted into the item info lookup.');
            childItemInfo.gridRowIndex = parentRowIndex + 1 + i;
        }
    };
    /**
     * Removes the children of the given row from the rows array and updates their itemInfo
     */
    NestedRowStore.prototype.collapseRowAtIndex = function (rowIndex) {
        if (!this.childRowsExist(rowIndex)) {
            return;
        }
        this.setRowIsExpanded(rowIndex, false);
        // remove the children from rows
        var itemInfo = this.itemInfoLookup[this.rows[rowIndex].key];
        this.rows.splice(rowIndex + 1, itemInfo.numChildren);
        // update the children's gridRowIndexes in itemInfoLookup
        for (var i = 0; i < itemInfo.numChildren; i++) {
            var childDomainItem = this.items[itemInfo.domainIndex + 1 + i];
            var childItemInfo = this.itemInfoLookup[this.getKey(childDomainItem)];
            childItemInfo.gridRowIndex = -1;
        }
    };
    NestedRowStore.prototype.childRowsExist = function (parentRowIndex) {
        if (parentRowIndex >= this.rows.length - 1) {
            return false;
        }
        // child rows are always inserted at parentIndex + 1, so check the next row's parentKey to see if it's a child
        return this.rows[parentRowIndex].key === this.rows[parentRowIndex + 1].parentKey;
    };
    NestedRowStore.prototype.setRowIsExpanded = function (rowIndex, isExpanded) {
        var row = this.rows[rowIndex];
        row.isExpanded = isExpanded;
        var newRow = tslib_1.__assign({}, row);
        this.rows[rowIndex] = newRow; // reassign reference so React knows to update the row
    };
    NestedRowStore.prototype.isRowExpanded = function (rowKey) {
        var hasKey = this.expandedRowKeys[rowKey] !== undefined;
        return hasKey && this.expandedRowKeys[rowKey] === true;
    };
    return NestedRowStore;
}());
exports.NestedRowStore = NestedRowStore;
//# sourceMappingURL=NestedRowStore.js.map